import React, { useState, useEffect } from 'react';
import { HTRowProps, RowState, ActionType } from './types';
import { formatDateTime, DateTimeDisplay, formatSecondsToHMS } from './utils';
import { getDisplayName } from '../../utils/displayUtils';

/**
 * A component that renders a handling tier row in the hierarchical table.
 * This represents a non-default handling tier under a production capability.
 */
export const HandlingTierRow: React.FC<HTRowProps> = ({
    tier,
    pcId,
    pcName,
    pendingChange,
    parentPcDeletePending,
    onValueChange,
    onDelete,
    userCanEdit,
    warehouseId
}) => {
    // Apply Default_Network_PC editing restrictions (same as R2F implementation)
    const isDefaultNetworkPC = pcName === 'Default_Network_PC';
    const canEditDefaultNetworkPC = !isDefaultNetworkPC || (warehouseId === 'DEFAULT');
    const effectiveUserCanEdit = userCanEdit && canEditDefaultNetworkPC;
    // Check if this is a default tier or a regular handling tier
    const isDefaultTier = tier.isDefault;
    // Validation state
    const [inputValue, setInputValue] = useState<string>('');
    const [hasManualInput, setHasManualInput] = useState<boolean>(false);
    const [selectedAction, setSelectedAction] = useState<string>('NoAction');

    const getActionState = (): RowState => {
        if (pendingChange) {
            if (pendingChange.action === ActionType.RECOMMENDED) {
                return RowState.RECOMMENDATION_ACCEPTED;
            } else if (pendingChange.action === ActionType.DELETE) {
                return RowState.DELETE_PENDING;
            } else if (pendingChange.action !== ActionType.NO_ACTION) {
                return RowState.OVERRIDE_PENDING;
            }
        }

        if (parentPcDeletePending) {
            return RowState.PARENT_DELETE_PENDING;
        }
        
        return RowState.UNCHANGED;
    };

    const getRecommendationState = (): { state: string, className: string } => {
        if (tier.recommendedGSLA !== undefined && 
            tier.currentGSLA !== undefined) {
            if (tier.recommendedGSLA > tier.currentGSLA) {
                return { state: 'higher', className: 'higher-recommendation' };
            } else if (tier.recommendedGSLA < tier.currentGSLA) {
                return { state: 'lower', className: 'lower-recommendation' };
            }
        }
        
        return { state: '', className: '' };
    };
    
    const actionState = getActionState();
    const recommendationState = getRecommendationState();
    const rowClassName = `handling-tier-row ${actionState}`;

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setInputValue(value);
        
        // Only mark as having manual input if there's actually a value
        setHasManualInput(value.trim() !== '');
        
        // If we have a valid numeric value and a specific action, update the value
        if (value.trim() !== '' && selectedAction !== 'NoAction' && selectedAction !== 'Recommended') {
            const numValue = parseInt(value);
            if (!isNaN(numValue)) {
                onValueChange(pcId, tier.id, numValue, selectedAction);
            }
        }
    };
    
    // Handle action change
    const handleActionChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const action = e.target.value;
        setSelectedAction(action);
        
        // If accepting recommendation, use the recommended value
        if (action === 'Recommended' && tier.recommendedGSLA !== undefined) {
            onValueChange(pcId, tier.id, tier.recommendedGSLA, action);
        } 
        // If choosing a specific reason and we have a manual input value, use it
        else if (action !== 'NoAction' && hasManualInput && inputValue.trim() !== '') {
            const numValue = parseInt(inputValue);
            if (!isNaN(numValue)) {
                onValueChange(pcId, tier.id, numValue, action);
            }
        }
        // If choosing "No Action", remove any pending changes
        else if (action === 'NoAction') {
            onValueChange(pcId, tier.id, 0, 'NoAction');
        }
    };

    const handleDelete = () => {
        // If it's already marked for deletion, cancel it by setting to NO_ACTION
        if (pendingChange?.action === ActionType.DELETE) {
            onValueChange(pcId, tier.id, 0, ActionType.NO_ACTION);
        } else {
            // Otherwise mark it for deletion
            onValueChange(pcId, tier.id, 0, ActionType.DELETE);
        }
    };

    useEffect(() => {
        if (pendingChange) {
            setSelectedAction(pendingChange.action);
            
            // Only update the input value if the action is not "Recommended" and newGSLA is defined
            if (pendingChange.action !== 'Recommended' && pendingChange.newGSLA !== undefined) {
                setInputValue(pendingChange.newGSLA.toString());
                setHasManualInput(true);
            }
        } else {
            setSelectedAction('NoAction');
            setInputValue('');
            setHasManualInput(false);
        }
    }, [pendingChange]);

    const needsValue = selectedAction !== 'NoAction' && 
                       selectedAction !== 'Recommended' && 
                       !hasManualInput;
                       
    const needsReason = hasManualInput && 
                      (selectedAction === 'NoAction' || selectedAction === '' || selectedAction === undefined);
    
    const isValidationIncomplete = needsValue || needsReason;

    const hasRecommendation = tier.recommendedGSLA !== undefined;
    
    return (
        <tr className={rowClassName}>
            <td className="expand-cell"></td>
            <td className={`${isDefaultTier ? 'default-indent' : 'indent'} pc-name`}>{getDisplayName(pcName)}</td>
            <td className={`tier-name ${isDefaultTier ? 'default-tier-name' : ''}`}>
                {tier.name}
                {isDefaultTier && <span className="default-badge"> (Default)</span>}
            </td>
            <td className="current-gsla">
                {tier.currentGSLA !== undefined ? (
                    <div className="gsla-display" title={formatSecondsToHMS(tier.currentGSLA)}>
                        <span>{tier.currentGSLA}s</span>
                        <span className="info-icon" role="img" aria-label="Info">ⓘ</span>
                    </div>
                ) : 'N/A'}
            </td>
            <td className="published-date">
                {(() => {
                    const dateInfo = formatDateTime(tier.publishDateEpochMillis ? 
                        new Date(tier.publishDateEpochMillis) : undefined);
                    return (
                        <div className="datetime-display" title={dateInfo.fullDateTime}>
                            <span className="relative-time">{dateInfo.relativeTime}</span>
                            {dateInfo.fullDateTime && <span className="info-icon" role="img" aria-label="Info">ⓘ</span>}
                        </div>
                    );
                })()}
            </td>
            <td className="recommended-gsla">
                {tier.recommendedGSLA !== undefined ? (
                    <div className={`gsla-display ${recommendationState.className}`} title={formatSecondsToHMS(tier.recommendedGSLA)}>
                        <span>{tier.recommendedGSLA}s</span>
                        <span className="info-icon" role="img" aria-label="Info">ⓘ</span>
                    </div>
                ) : (isDefaultTier ? 'N/A' : '')}
            </td>
            <td className="recommended-date">
                {(() => {
                    const dateInfo = formatDateTime(tier.recommendationDateEpochMillis ?
                        new Date(tier.recommendationDateEpochMillis) : undefined);
                    // Only show "No entry" for default tiers, leave empty for non-default tiers
                    if (!dateInfo.fullDateTime && !isDefaultTier) {
                        return '';
                    }
                    return (
                        <div className="datetime-display" title={dateInfo.fullDateTime}>
                            <span className="relative-time">{dateInfo.relativeTime}</span>
                            {dateInfo.fullDateTime && <span className="info-icon" role="img" aria-label="Info">ⓘ</span>}
                        </div>
                    );
                })()}
            </td>
            {userCanEdit && (
                <>
                    <td className={`override-cell ${needsValue ? 'validation-incomplete' : ''}`}>
                        {effectiveUserCanEdit ? (
                            <input
                                type="number"
                                className={`override-input ${needsValue ? 'needs-value' : ''}`}
                                value={inputValue}
                                onChange={handleInputChange}
                                placeholder="Enter value"
                                disabled={selectedAction === 'Recommended'}
                            />
                        ) : (
                            <span className="disabled-info">Not editable on FC pages</span>
                        )}
                    </td>
                    <td className={`action-cell ${needsReason ? 'validation-incomplete' : ''}`}>
                        <div className="action-container">
                            {effectiveUserCanEdit ? (
                                <>
                                    <select
                                        className={`action-select ${needsReason ? 'needs-reason' : ''}`}
                                        value={selectedAction}
                                        onChange={handleActionChange}
                                    >
                                        <option value="NoAction">No Action</option>
                                        <option value="Recommended" disabled={!hasRecommendation} title={!hasRecommendation ? "No recommendation available" : ""}>
                                            Accept Recommended
                                        </option>
                                        <option value="HVE">High Volume Event</option>
                                        <option value="NewFC">FC Launch</option>
                                        <option value="MHEChange">Upcoming MHE change/retrofit</option>
                                        <option value="DEAPerformance">DEA Performance has deteriorated</option>
                                        <option value="Outliers">Outlier Events</option>
                                    </select>
                                    {onDelete && (
                                        <button
                                            className="delete-tier-button"
                                            onClick={handleDelete}
                                            aria-label={`${pendingChange?.action === ActionType.DELETE ? 'Cancel delete' : 'Delete'} ${tier.name} handling tier`}
                                            title={pendingChange?.action === ActionType.DELETE ? "Cancel deletion" : "Delete this handling tier"}
                                        >
                                            {pendingChange?.action === ActionType.DELETE ? '↩️' : '🗑️'}
                                        </button>
                                    )}
                                </>
                            ) : (
                                <span className="disabled-info">Default_Network_PC editing restricted to DEFAULT warehouse</span>
                            )}
                        </div>
                    </td>
                </>
            )}
            <td className="status-cell">
                {actionState === RowState.RECOMMENDATION_ACCEPTED && <span className="status-badge recommendation">RECOMMENDATION ACCEPTED</span>}
                {actionState === RowState.OVERRIDE_PENDING && <span className="status-badge override">OVERRIDE PENDING</span>}
                {actionState === RowState.DELETE_PENDING && <span className="status-badge delete">DELETE PENDING</span>}
                {actionState === RowState.PARENT_DELETE_PENDING && <span className="status-badge delete">PARENT PC DELETED</span>}
            </td>
        </tr>
    );
};
